import * as Sentry from "@sentry/nextjs";
import { env } from "@shared/env.mjs";
import type { CreateContextOptions } from "@shared-server/trpc";
import type { AppEnumType } from "@shared/database/enums";
import type { TRPCError } from "@trpc/server";
import { developmentEnvironments } from "@shared-utils/environments";

/**
 * Handles a tRPC error by logging it and emitting a log event.
 * @param path - The path where the tRPC error occurred.
 * @param error - The tRPC error object.
 * @param ctx - The context options for the tRPC request.
 * @param input - The input object passed to the tRPC request.
 */
export const logTrpcError = (
  path: string | undefined,
  error: TRPCError,
  ctx: CreateContextOptions | undefined,
  input: unknown,
  app: AppEnumType,
) => {
  if (error.code !== "UNAUTHORIZED") {
    // Log to stderr
    console.error(
      `❌ tRPC failed in ${app} with ${error.code} on ${path ?? "<no-path>"}: ${error.message}. Input: ${JSON.stringify(input)}. Stack: ${error.stack}`,
    );
    // Log to sentry
    logError(
      error,
      `tRPC failed in ${app} with ${error.code} on ${path ?? "<no-path>"}: ${error.message}`,
      app,
      {
        input,
        path,
        error,
        session: ctx?.session,
        validCsrf: ctx?.validCsrf,
        userIp: ctx?.userIp,
        workspaceId: ctx?.workspaceId,
      },
    );
  }
};

/**
 * Log information to the console and emit a log event.
 * @param message - The message to log.
 * @param severityNumber - The severity number of the log message.
 * @param app - The app that the log message is associated with.
 * @param attributes - Additional attributes to include in the log message.
 * @param ctx - The context options for the tRPC request.
 */
export const logError = (
  error: unknown,
  message: string,
  app: AppEnumType,
  attributes: Record<string, unknown> = {},
) => {
  // Log to stderr locally
  if (developmentEnvironments.has(env.ENV)) {
    console.error(error);
  }

  Sentry.captureException(error, {
    extra: {
      message,
      app,
      ...attributes,
    },
  });
};

export const logFrontendError = (
  error: unknown,
  message: string,
  app: AppEnumType,
  attributes: Record<string, unknown> = {},
) => {
  console.error(error);
  Sentry.captureException(error, {
    extra: {
      message,
      app,
      ...attributes,
    },
  });
};

/**
 * Track metric usage event via sentry
 * @param metricName - The name of the tool being used
 * @param success - Whether the tool execution was successful
 * @param additionalAttributes - Any additional attributes to include in the event
 */
export const trackMetric = async <T>(
  info: {
    metricName: string;
    appName?: "shared" | "chat-ai" | "training-ai" | "translate-ai";
    additionalAttributes?: Record<
      string,
      string | number | boolean | undefined
    >;
  },
  callback: () => Promise<T>,
): Promise<T> => {
  // Default values
  const appName = info.appName ?? "shared";
  const additionalAttributes = info.additionalAttributes ?? {};
  // Wrap in span to track metric / callback
  return await Sentry.startSpan(
    {
      name: `metric-${info.metricName}`,
      forceTransaction: true,
      attributes: {
        appName,
        ...additionalAttributes,
      },
    },
    async () => {
      return await callback();
    },
  );
};
